import React, { useState, useEffect, useContext, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { AppContext } from './App.js';
import { CheckCircle2, MessageSquareWarning, Loader2, AlertTriangle, Film, ShieldCheck, Send, Download } from 'lucide-react';

// --- Public, no-login client review page ---
// Reached via https://blackbox-cr.com/?page=review&token=XXXX
//
// Backend contract this page expects from `getReviewData({ token })`:
//   {
//     projectName, clientLabel, decision,       // unchanged
//     credits: [...],                            // unchanged — shared across all deliverables
//     deliverables: [
//       { jobId, label, exportType, url, sizeBytes },
//       ...
//     ]
//   }
// `label` is what's shown on the tab (e.g. "Scroll Credits", "Title Cards");
// falls back to `exportType` if absent. `sizeBytes` drives the small-file-
// first ordering below — if it's missing on every item, original order is
// kept as-is rather than guessing.
//
// Backward compat: a link created before this multi-file support existed
// returns the OLD single-file shape ({ url, ... }, no `deliverables`). That
// still works — it's normalized into a one-item deliverables array below —
// so existing review links already sent to clients don't break.
const normalizeDeliverables = (result) => {
    if (Array.isArray(result?.deliverables) && result.deliverables.length > 0) {
        const list = [...result.deliverables];
        const allHaveSize = list.every(d => typeof d.sizeBytes === 'number');
        if (allHaveSize) list.sort((a, b) => a.sizeBytes - b.sizeBytes); // small file first
        return list;
    }
    // Old single-file response shape.
    if (result?.url) {
        return [{ jobId: result.jobId, label: result.label, exportType: result.exportType, url: result.url, sizeBytes: result.sizeBytes }];
    }
    return [];
};

const formatBytes = (bytes) => {
    if (typeof bytes !== 'number' || !isFinite(bytes) || bytes <= 0) return null;
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
    return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const ReviewPage = ({ token }) => {
    const { functions } = useContext(AppContext);

    const [state, setState] = useState('loading'); // loading | ready | error
    const [errorMessage, setErrorMessage] = useState('');
    const [data, setData] = useState(null);
    const [deliverables, setDeliverables] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0); // which deliverable tab is showing — defaults to the smallest file after sorting

    const [decision, setDecision] = useState(null); // null | 'approved' | 'changes_requested'
    const [note, setNote] = useState('');
    const [showNoteField, setShowNoteField] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // --- In-browser preview support (ProRes / some MOV codecs can't decode
    // in any browser — Chrome, Firefox, and Edge don't ship a ProRes
    // decoder at all, and Safari's support is inconsistent by profile).
    // Keyed by deliverable index, since each file previews independently —
    // e.g. an H.264 Scroll render might play fine while a ProRes Cards
    // render (or vice versa) needs the download fallback.
    // null = still checking, true = plays fine, false = show the download
    // fallback instead of a dead gray video box.
    const [canPreviewMap, setCanPreviewMap] = useState({});
    const canPreview = canPreviewMap[activeIndex] ?? null;

    // --- Forced download state (separate from the review-decision error,
    // since a download can fail independently of everything else on this page).
    const [downloading, setDownloading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(null); // 0-100, or null when unknown (no content-length)
    const [finalizingDownload, setFinalizingDownload] = useState(false); // true during the blob-assembly gap after all bytes are in but before the save actually triggers — can take a real moment on huge files
    const [justDownloaded, setJustDownloaded] = useState(false); // brief confirmation window after a save fires, so it's obvious something happened instead of the button just quietly going back to idle
    const [downloadError, setDownloadError] = useState('');

    const videoRef = useRef(null);
    const activeDeliverable = deliverables[activeIndex] || null;

    useEffect(() => {
        if (!functions || !token) return;
        let cancelled = false;

        (async () => {
            try {
                const getReviewData = httpsCallable(functions, 'getReviewData');
                const { data: result } = await getReviewData({ token });
                if (cancelled) return;
                setData(result);
                setDeliverables(normalizeDeliverables(result));
                setDecision(result.decision === 'pending' ? null : result.decision);
                setState('ready');
            } catch (error) {
                if (cancelled) return;
                setErrorMessage(error.message || 'This review link could not be loaded.');
                setState('error');
            }
        })();

        return () => { cancelled = true; };
    }, [functions, token]);

    // --- Detect an undecodable video (ProRes etc.) ---
    // Two signals, whichever comes first:
    //  1. The 'error' event — fires reliably in Chrome/Firefox/Edge when the
    //     codec can't be decoded at all.
    //  2. A timeout — Safari sometimes just stalls at readyState 0 with no
    //     'error' event for a codec/profile it can't handle, so if nothing
    //     has loaded after a few seconds we treat that as unsupported too.
    // Re-runs on every tab switch (activeIndex) — each deliverable is
    // checked independently, since one file's codec support says nothing
    // about another's.
    useEffect(() => {
        if (state !== 'ready' || !activeDeliverable?.url) return;
        const el = videoRef.current;
        if (!el) return;

        let settled = false;
        const markPlayable = () => { if (!settled) { settled = true; setCanPreviewMap(prev => ({ ...prev, [activeIndex]: true })); } };
        const markUnsupported = () => { if (!settled) { settled = true; setCanPreviewMap(prev => ({ ...prev, [activeIndex]: false })); } };

        el.addEventListener('loadeddata', markPlayable);
        el.addEventListener('error', markUnsupported);

        const timeoutId = setTimeout(() => {
            // el.readyState >= 2 (HAVE_CURRENT_DATA) means a frame actually
            // decoded, even if 'loadeddata' hasn't technically fired yet.
            if (el.readyState >= 2) markPlayable();
            else markUnsupported();
        }, 7000);

        return () => {
            clearTimeout(timeoutId);
            el.removeEventListener('loadeddata', markPlayable);
            el.removeEventListener('error', markUnsupported);
        };
    }, [state, activeIndex, activeDeliverable]);

    // Best-effort filename for the downloaded file: the signed URL's own
    // path if we can parse one, otherwise fall back to the project name.
    const guessFileName = () => {
        try {
            const parsed = new URL(activeDeliverable.url);
            const base = decodeURIComponent(parsed.pathname.split('/').pop() || '');
            if (base) return base;
        } catch (err) { /* fall through to the generated name below */ }
        const safeName = (data?.projectName || 'credits-export').replace(/[^\w\-]+/g, '_');
        const suffix = activeDeliverable?.label ? `_${activeDeliverable.label.replace(/[^\w\-]+/g, '_')}` : '';
        return `${safeName}${suffix}.mov`;
    };

    // --- Forced download ---
    // A plain <a href={data.url}> only forces a download for same-origin
    // URLs; for a cross-origin signed GCS URL, clicking it just navigates
    // there (and since the browser can't render ProRes, that's the gray
    // screen the reviewer already saw once). Fetching the bytes ourselves
    // and handing the browser a blob: URL forces an actual save-to-disk
    // every time, regardless of what the storage server's headers say.
    //
    // This reads the response as a stream instead of awaiting response.blob()
    // directly, for two reasons:
    //   1. Real progress. A multi-GB ProRes master can take minutes to fully
    //      buffer client-side with zero feedback otherwise — "Preparing
    //      Download…" with no percentage is indistinguishable from actually
    //      being stuck. Reading chunk-by-chunk lets the button show real %.
    //   2. Real stall detection. If a chunk hasn't arrived in 20s, we abort
    //      and surface an error instead of spinning forever — same UI state
    //      that motivated this rewrite. Not every browser exposes a
    //      streaming response body, so there's a plain response.blob()
    //      fallback for those (silent/no progress, same as before, but still
    //      subject to the stall check via the fetch's own abort signal).
    const handleDownload = async () => {
        if (!activeDeliverable?.url || downloading) return;
        setDownloading(true);
        setDownloadError('');
        setDownloadProgress(null);

        const controller = new AbortController();
        let lastProgressAt = Date.now();
        const STALL_TIMEOUT_MS = 20000;
        const stallCheck = setInterval(() => {
            if (Date.now() - lastProgressAt > STALL_TIMEOUT_MS) controller.abort();
        }, 2000);

        try {
            const response = await fetch(activeDeliverable.url, { signal: controller.signal });
            if (!response.ok) throw new Error(`Download failed (${response.status})`);

            const totalBytes = Number(response.headers.get('content-length')) || 0;
            let blob;

            if (response.body && response.body.getReader) {
                const reader = response.body.getReader();
                const chunks = [];
                let receivedBytes = 0;
                // eslint-disable-next-line no-constant-condition
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    chunks.push(value);
                    receivedBytes += value.length;
                    lastProgressAt = Date.now();
                    if (totalBytes) setDownloadProgress(Math.round((receivedBytes / totalBytes) * 100));
                }
                blob = new Blob(chunks);
            } else {
                // No streaming body support (older browser) — same behavior as before.
                blob = await response.blob();
            }

            // Blob assembly above can itself take a real moment on a multi-GB
            // file — this is the gap that used to look like nothing was
            // happening right after hitting 100%. Surface it explicitly.
            setFinalizingDownload(true);

            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = guessFileName();
            document.body.appendChild(link);
            link.click();
            link.remove();
            // Give the browser a moment to pick up the save before revoking.
            setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);

            // The save is already fully automatic at this point — no second
            // click is ever required. What was missing was confirmation:
            // without this, the button just quietly reverts to "Download
            // File" and it's not obvious anything happened, so people click
            // it again out of doubt. This makes success visible for a beat.
            setJustDownloaded(true);
            setTimeout(() => setJustDownloaded(false), 4000);
        } catch (err) {
            console.error('Download failed:', err);
            if (err.name === 'AbortError') {
                setDownloadError('Download stalled with no progress for 20s — use "Open File Directly" below instead.');
            } else {
                setDownloadError('Automatic download failed — use "Open File Directly" below, then save it from there.');
            }
        } finally {
            clearInterval(stallCheck);
            setDownloading(false);
            setDownloadProgress(null);
            setFinalizingDownload(false);
        }
    };

    const handleDecision = async (chosenDecision) => {
        if (chosenDecision === 'changes_requested' && !showNoteField) {
            setShowNoteField(true);
            return;
        }
        setSubmitting(true);
        try {
            const submitReviewDecision = httpsCallable(functions, 'submitReviewDecision');
            await submitReviewDecision({ token, decision: chosenDecision, note });
            setDecision(chosenDecision);
            setSubmitted(true);
        } catch (error) {
            setErrorMessage(error.message || 'Could not submit your response. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    // Single source of truth for what a download button should show right
    // now — used by both the fallback-panel button and the Master File bar
    // button. They drifted out of sync once already (one got progress text,
    // the other stayed hardcoded on "Preparing…" forever); this stops that
    // from happening again by construction.
    const downloadButtonState = (iconSize) => {
        if (justDownloaded) return { icon: <CheckCircle2 size={iconSize} className="text-emerald-400" />, label: 'Downloaded' };
        if (finalizingDownload) return { icon: <Loader2 size={iconSize} className="animate-spin" />, label: 'Finalizing…' };
        if (downloading) return { icon: <Loader2 size={iconSize} className="animate-spin" />, label: downloadProgress !== null ? `Downloading… ${downloadProgress}%` : 'Starting…' };
        return { icon: <Download size={iconSize} />, label: 'Download' };
    };

    // --- Loading ---
    if (state === 'loading') {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center font-poppins">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 size={28} className="animate-spin text-yellow-brand" />
                    <p className="text-[10px] text-zinc-600 font-black uppercase tracking-[0.3em]">Loading Screening Room</p>
                </div>
            </div>
        );
    }

    // --- Error / expired / revoked ---
    if (state === 'error') {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center font-poppins px-4">
                <div className="max-w-md w-full text-center bg-zinc-950 border border-zinc-800 rounded-2xl p-10">
                    <AlertTriangle size={32} className="mx-auto mb-5 text-red-500" />
                    <h1 className="text-lg font-black text-white uppercase tracking-tight mb-2">Link Unavailable</h1>
                    <p className="text-sm text-zinc-500 leading-relaxed">{errorMessage}</p>
                </div>
            </div>
        );
    }

    const alreadyDecided = decision === 'approved' || decision === 'changes_requested';

    return (
        <div className="min-h-screen bg-black font-poppins text-zinc-100">
            {/* Header */}
            <div className="border-b border-zinc-900 px-6 py-5 flex items-center justify-between">
                <p className="text-base font-black tracking-tight text-white">
                    BLACKBOX<span className="text-zinc-600">cr</span>
                </p>
                <div className="flex items-center gap-2 text-[9px] font-black uppercase tracking-[0.25em] text-zinc-600">
                    <ShieldCheck size={14} className="text-zinc-700" />
                    Private Screening Room
                </div>
            </div>

            <div className="max-w-3xl mx-auto px-6 py-10">
                <div className="mb-8">
                    {data.clientLabel && (
                        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-yellow-brand mb-2">
                            Prepared for {data.clientLabel}
                        </p>
                    )}
                    <h1 className="text-2xl md:text-3xl font-black text-white tracking-tight uppercase">
                        "{data.projectName}"
                    </h1>
                    <p className="text-xs text-zinc-600 mt-2 flex items-center gap-2">
                        <Film size={13} /> Credits sequence for review — no download or account required.
                    </p>
                </div>

                {/* Deliverable tabs — only shown when a link bundles more than one
                    file (e.g. Scroll + Cards). Smallest file is active by default
                    (normalizeDeliverables sorts by sizeBytes), so the reviewer's
                    first look loads fast regardless of which format it is. */}
                {deliverables.length > 1 && (
                    <div className="flex items-center gap-2 mb-4">
                        {deliverables.map((d, i) => {
                            const sizeLabel = formatBytes(d.sizeBytes);
                            const label = d.label || d.exportType || `File ${i + 1}`;
                            return (
                                <button
                                    key={d.jobId || i}
                                    onClick={() => setActiveIndex(i)}
                                    className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-[0.15em] transition-all border ${
                                        i === activeIndex
                                            ? 'text-black border-transparent'
                                            : 'bg-zinc-950 border-zinc-800 text-zinc-500 hover:text-zinc-300 hover:border-zinc-700'
                                    }`}
                                    style={i === activeIndex ? { backgroundColor: '#FACC15' } : undefined}
                                >
                                    {label}{sizeLabel ? ` · ${sizeLabel}` : ''}
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Video / full-quality delivery */}
                <div className="rounded-2xl overflow-hidden border border-zinc-800 bg-zinc-950 shadow-2xl mb-8">
                    {canPreview === false ? (
                        <div className="aspect-video flex flex-col items-center justify-center text-center px-8 gap-5 bg-gradient-to-b from-zinc-950 to-black">
                            <div className="w-14 h-14 rounded-full bg-yellow-brand/10 border border-yellow-brand/30 flex items-center justify-center">
                                <Film size={22} className="text-yellow-brand" />
                            </div>
                            <div>
                                <p className="text-sm font-black text-white uppercase tracking-[0.15em]">Full-Quality Master File</p>
                                <p className="text-xs text-zinc-500 mt-2 max-w-sm leading-relaxed">
                                    This delivery is provided at full resolution for the sharpest possible review — open it in QuickTime, DaVinci Resolve, Premiere, or your NLE of choice.
                                </p>
                            </div>
                            <div className="flex flex-col items-center gap-2">
                                {(() => {
                                    const btn = downloadButtonState(16);
                                    return (
                                        <button
                                            onClick={handleDownload}
                                            disabled={downloading}
                                            // [VISIBILITY FIX] bg-yellow-brand is a custom theme color; if this
                                            // public route's build doesn't include that theme extension, the
                                            // class silently generates no CSS and the button renders with no
                                            // background at all (only hover:bg-yellow-300 — a standard,
                                            // always-available Tailwind color — was ever showing). Setting the
                                            // color via inline style guarantees it renders regardless of
                                            // theme/build config.
                                            style={{ backgroundColor: justDownloaded ? undefined : '#FACC15' }}
                                            className={`inline-flex items-center justify-center gap-2 px-6 py-3 font-black text-xs tracking-[0.15em] uppercase rounded-xl transition-all active:scale-95 disabled:opacity-50 ${
                                                justDownloaded ? 'bg-emerald-950/40 border border-emerald-800/50 text-emerald-300' : 'text-black hover:brightness-90'
                                            }`}
                                        >
                                            {btn.icon}
                                            {justDownloaded ? 'Downloaded' : (downloading || finalizingDownload ? btn.label : 'Download File')}
                                        </button>
                                    );
                                })()}
                                <a
                                    href={activeDeliverable?.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[10px] text-zinc-600 hover:text-zinc-400 underline underline-offset-2"
                                >
                                    Open File Directly
                                </a>
                                {downloadError && <p className="text-xs text-red-500 mt-1 max-w-sm">{downloadError}</p>}
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div className="relative">
                                <video key={activeIndex} ref={videoRef} src={activeDeliverable?.url} controls playsInline className="w-full aspect-video bg-black" />
                                {canPreview === null && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 pointer-events-none">
                                        <Loader2 size={22} className="animate-spin text-zinc-500" />
                                    </div>
                                )}
                            </div>

                            {/* Dedicated download control — same blob-fetch handleDownload as the
                                fallback path above, just surfaced intentionally instead of leaving
                                the browser's video-controls overflow menu as the only way out. */}
                            {canPreview === true && (
                                <div className="flex items-center justify-between gap-4 px-5 py-3.5 border-t border-zinc-900 bg-zinc-950">
                                    <div className="min-w-0">
                                        <p className="text-[9px] font-black uppercase tracking-[0.25em] text-zinc-600">Master File</p>
                                        <p className="text-xs text-zinc-500 truncate mt-0.5">{guessFileName()}</p>
                                    </div>
                                    <button
                                        onClick={handleDownload}
                                        disabled={downloading}
                                        className={`shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2.5 border font-black text-[10px] tracking-[0.2em] uppercase rounded-xl transition-all active:scale-95 disabled:opacity-50 ${
                                            justDownloaded
                                                ? 'bg-emerald-950/40 border-emerald-800/50 text-emerald-300'
                                                : 'bg-zinc-900 border-zinc-800 text-zinc-200 hover:border-yellow-brand/60 hover:text-yellow-brand'
                                        }`}
                                    >
                                        {downloadButtonState(14).icon}
                                        {downloadButtonState(14).label}
                                    </button>
                                </div>
                            )}
                            {downloadError && canPreview === true && (
                                <p className="text-xs text-red-500 px-5 pb-3.5 bg-zinc-950">{downloadError}</p>
                            )}
                        </div>
                    )}
                </div>

                {/* Credits list, for spelling / accuracy checks alongside the video */}
                {data.credits && data.credits.length > 0 && (
                    <div className="mb-10 bg-zinc-950 border border-zinc-900 rounded-2xl p-6">
                        <p className="text-[9px] font-black uppercase tracking-[0.25em] text-zinc-600 mb-4">Credit List</p>
                        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
                            {data.credits.map((c, i) => (
                                <div key={i} className="flex justify-between gap-4 text-sm py-1.5 border-b border-zinc-900/80">
                                    <span className="text-zinc-500">{c.role}</span>
                                    <span className="text-zinc-200 font-medium text-right">{c.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Decision */}
                {submitted || alreadyDecided ? (
                    <div className={`rounded-2xl p-8 text-center border ${decision === 'approved' ? 'bg-emerald-950/20 border-emerald-900/40' : 'bg-amber-950/20 border-amber-900/40'}`}>
                        {decision === 'approved' ? (
                            <>
                                <CheckCircle2 size={28} className="mx-auto mb-3 text-emerald-400" />
                                <p className="text-sm font-bold text-emerald-300 uppercase tracking-wide">Approved</p>
                                <p className="text-xs text-zinc-500 mt-2">Thanks — the production team has been notified.</p>
                            </>
                        ) : (
                            <>
                                <MessageSquareWarning size={28} className="mx-auto mb-3 text-amber-400" />
                                <p className="text-sm font-bold text-amber-300 uppercase tracking-wide">Changes Requested</p>
                                <p className="text-xs text-zinc-500 mt-2">Thanks — your notes have been sent to the production team.</p>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="bg-zinc-950 border border-zinc-900 rounded-2xl p-6">
                        <p className="text-[9px] font-black uppercase tracking-[0.25em] text-zinc-600 mb-5">Your Response</p>

                        {showNoteField && (
                            <textarea
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="What needs to change? (spelling, order, timing, etc.)"
                                rows={4}
                                className="w-full mb-4 bg-black border border-zinc-800 rounded-xl px-4 py-3 text-sm text-zinc-200 placeholder-zinc-700 focus:outline-none focus:border-yellow-brand/50"
                            />
                        )}

                        <div className="flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={() => handleDecision('approved')}
                                disabled={submitting}
                                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 bg-yellow-brand text-black font-black text-xs tracking-[0.15em] uppercase rounded-xl hover:bg-yellow-300 transition-all active:scale-95 disabled:opacity-50"
                            >
                                {submitting ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />}
                                Approve Credits
                            </button>
                            <button
                                onClick={() => handleDecision('changes_requested')}
                                disabled={submitting}
                                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 bg-zinc-900 text-zinc-200 border border-zinc-800 font-black text-xs tracking-[0.15em] uppercase rounded-xl hover:bg-zinc-800 transition-all active:scale-95 disabled:opacity-50"
                            >
                                {showNoteField ? <Send size={18} /> : <MessageSquareWarning size={18} />}
                                {showNoteField ? 'Send Notes' : 'Request Changes'}
                            </button>
                        </div>
                        {errorMessage && <p className="text-xs text-red-500 mt-4">{errorMessage}</p>}
                    </div>
                )}

                <p className="text-center text-[10px] text-zinc-700 mt-10">
                    Powered by BLACKBOXcr
                </p>
            </div>
        </div>
    );
};

export default ReviewPage;